import { Block } from '../../types/sprintLessonTypes';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import { useCallback } from 'react';
import { shallow } from 'zustand/shallow';

// Enable Map and Set support in Immer
enableMapSet();

// NOTE: Maps have limitations with React DevTools and persistence layers
// They may appear as <unserializable> in DevTools or be lost during persistence
// If you need to persist this store or debug with DevTools, consider converting
// pagesData to a plain object before serialization and back to a Map after deserialization

// Page configuration from the lesson schema
export interface PageConfig {
  pageIndex: number;
  pageFieldName: string;
  pageType: 'read_only_content' | 'one_user_activity';
  blocks: Record<string, Block>; // blocks organized by their field name within the page
  orderedFields?: string[]; // Optional ordered list of field names for this page (from schema)
}

// Define a type for the lesson schema to improve type safety
export interface LessonSchemaProperty {
  type?: string;
  properties?: Record<string, LessonSchemaProperty>;
  $ref?: string;
  const?: string;
  enum?: string[];
  items?: LessonSchemaProperty;
}

export interface LessonSchema {
  properties: Record<string, LessonSchemaProperty>;
  $defs?: Record<string, LessonSchemaProperty>;
  required?: string[];
  title?: string;
  type?: string;
}

// Store state interface
interface MultiPageLessonState {
  // Lesson schema information
  lessonSchema: LessonSchema | null; // JSON schema of the lesson structure with improved type safety
  schemaFieldOrder: string[]; // Ordered list of page field names from schema
  
  // Page data organized by page index
  pagesData: Map<number, PageConfig>;
  
  // Feedback blocks organized (typically organized by feedback_for_block_id)
  feedbackBlocks: Map<string, Block>;
  
  // Current page navigation
  currentPageIndex: number;

  // User action payload for storing user interactions before submitting
  userActionPayload: Map<string, unknown>;
  setUserActionPayload: (payload: Map<string, unknown>) => void;
  getUserActionPayload: () => Map<string, unknown>;
  
  // Actions
  setLessonSchema: (schema: LessonSchema) => void;
  updatePageBlocks: (pageIndex: number, blocks: Block[], blocksToRemove?: string[]) => void;
  updateFeedbackBlock: (namespace: string, block: Block) => void;
  getFeedbackBlock: (namespace: string) => Block | undefined;
  setCurrentPage: (pageIndex: number) => void;
  getPageByIndex: (pageIndex: number) => PageConfig | undefined;
  getOrderedPages: () => PageConfig[];
  getTotalPages: () => number;
  reset: () => void;
}

// Extract field order from Pydantic schema based on structure, not naming convention
function extractFieldOrderFromSchema(schema: LessonSchema | null): string[] {
  if (!schema || !schema.properties) return [];
  
  // For multi-page lessons, all top-level fields that are objects with properties
  // are considered pages - no magic prefix required
  return Object.keys(schema.properties).filter(key => {
    const prop = schema.properties![key];
    // Consider it a page if it's an object with properties or references another schema
    return prop && typeof prop === 'object' && (
      prop.properties || // Has nested properties
      prop.$ref // References another schema
    );
  });
}

// Extract page type from schema definition
function extractPageTypeFromSchema(schema: LessonSchema | null, pageFieldName: string): 'read_only_content' | 'one_user_activity' {
  if (!schema || !schema.properties || !schema.properties[pageFieldName]) {
    return 'read_only_content'; // default fallback
  }
  
  let pageProperty = schema.properties[pageFieldName];
  
  // Handle schema references
  if (pageProperty.$ref && schema.$defs) {
    // Extract the definition name from the reference
    const refName = pageProperty.$ref.split('/').pop();
    if (refName && schema.$defs[refName]) {
      // Use the referenced schema definition
      pageProperty = schema.$defs[refName];
    }
  }
  
  // Look for page_type in the schema definition
  if (pageProperty.properties && pageProperty.properties.page_type) {
    const pageTypeProperty = pageProperty.properties.page_type;
    
    // Extract from enum or const
    if (pageTypeProperty.const) {
      return pageTypeProperty.const as 'read_only_content' | 'one_user_activity';
    }
    if (pageTypeProperty.enum && pageTypeProperty.enum.length > 0) {
      return pageTypeProperty.enum[0] as 'read_only_content' | 'one_user_activity';
    }
  }
  
  return 'read_only_content'; // default fallback
}

// Extract field name from block index_key or field_path - simplified version
function extractFieldNameFromIndexKey(key: string): string {
  // Handle any path format with dot notation, extracting everything after the first dot
  // Examples:
  // - "introduction.content" → "content"
  // - "exercise_1.question" → "question"
  // - "content" (no dots) → "content" (return as-is)
  const dotIndex = key.indexOf('.');
  return dotIndex >= 0 ? key.slice(dotIndex + 1) : key;
}

// Helper function for merging blocks to ensure new references are created
function mergeBlock(oldBlock: Block | undefined, incoming: Block): Block {
  return oldBlock ? { ...oldBlock, ...incoming } : { ...incoming };
}

// Create the Zustand store with Immer middleware
export const useMultiPageLessonStore = create(
  immer<MultiPageLessonState>((set, get) => ({
    // Initial state
    lessonSchema: null,
    schemaFieldOrder: [],
    pagesData: new Map(),
    feedbackBlocks: new Map(),
    currentPageIndex: 0,

    userActionPayload: new Map(),

    // Get user action payload
    getUserActionPayload: () => {
      return get().userActionPayload;
    },

    // Set user action payload
    setUserActionPayload: (payload: Map<string, unknown>) => {
      set(draft => {
        draft.userActionPayload = payload;
      });
    },
    
    // Actions
    setLessonSchema: (schema: LessonSchema) => {
      console.log("Setting lesson schema::::::", schema)
      const fieldOrder = extractFieldOrderFromSchema(schema);
      
      // With immer, we can directly mutate the draft state
      set(draft => {
        draft.lessonSchema = schema;
        draft.schemaFieldOrder = fieldOrder;
        draft.pagesData = new Map(); // Reset pages data when schema changes
        draft.currentPageIndex = 0;
        
        // Pre-initialize all pages from the schema with orderedFields
        // This ensures PageRenderer has orderedFields before any blocks arrive
        fieldOrder.forEach((pageFieldName, pageIndex) => {
          // Get the page type from schema
          const pageType = extractPageTypeFromSchema(schema, pageFieldName);
          
          // Extract orderedFields from the schema for this page
          const pageProperty = schema.properties[pageFieldName];
          const orderedFields = pageProperty && pageProperty.properties 
            ? Object.keys(pageProperty.properties)
            : [];
          
          // Create the page config with orderedFields
          draft.pagesData.set(pageIndex, {
            pageIndex,
            pageFieldName,
            pageType,
            orderedFields,
            blocks: {},
          });
        });
      });
    },
    
    updatePageBlocks: (pageIndex: number, blocks: Block[]) => {
      set(draft => {
        // Skip if we don't have a schema yet
        if (!draft.lessonSchema) {
          console.warn('Cannot update blocks: no lesson schema has been set');
          return;
        }

        if (blocks.length === 0) {
          // Nothing to update
          return;
        }
        
        // Get the field name for this page from the schema field order
        const pageFieldName = draft.schemaFieldOrder[pageIndex];
        
        if (!pageFieldName) {
          console.warn(`Cannot update blocks: no field name for page index ${pageIndex}`);
          return;
        }
        
        // Check if we have a page config for this page already
        if (!draft.pagesData.has(pageIndex)) {
          // This should not happen because page configs should be pre-initialized
          // when the schema is set. But handle it as a fallback.
          if (!draft.lessonSchema) {
            console.warn('Cannot create page config: no lesson schema has been set');
            return;
          }
          
          // This is unexpected, but recreate the page config as a fallback
          console.warn(`Page ${pageIndex} (${pageFieldName}) wasn't pre-initialized. Creating it now.`);
          
          const pageType = extractPageTypeFromSchema(draft.lessonSchema, pageFieldName);
          
          // Get ordered fields for this page from the schema if available
          const pageProperty = draft.lessonSchema.properties[pageFieldName];
          const orderedFields = pageProperty && pageProperty.properties 
            ? Object.keys(pageProperty.properties) 
            : [];
          
          // Create and add the new page config with orderedFields
          draft.pagesData.set(pageIndex, {
            pageIndex,
            pageFieldName,
            pageType,
            orderedFields,
            blocks: {},
          });
        }
        
        // Get the page config (we know it exists now)
        const pageConfig = draft.pagesData.get(pageIndex)!;
        
        // Update blocks - organize by their field name within the page
        blocks.forEach(block => {
          // Extract field name from block index_key using the more robust helper function
          const fieldName = extractFieldNameFromIndexKey(block.index_key);
          
          // Use our helper function to merge blocks and ensure new references
          pageConfig.blocks[fieldName] = mergeBlock(pageConfig.blocks[fieldName], block);
        });
      });
    },
    
    setCurrentPage: (pageIndex: number) => {
      // With immer, we can directly mutate the draft state
      set(draft => {
        draft.currentPageIndex = pageIndex;
      });
    },
  
    getPageByIndex: (pageIndex: number) => {
      return get().pagesData.get(pageIndex);
    },
    
    getOrderedPages: () => {
      // Simplified one-liner to get pages in order defined by schema
      return get().schemaFieldOrder
        .map((_, i) => get().pagesData.get(i))
        .filter(Boolean) as PageConfig[];
    },
    
    getTotalPages: () => {
      return get().schemaFieldOrder.length;
    },
    
    updateFeedbackBlock: (namespace: string, block: Block) => {
      // With immer, we can directly update the Map with the new block
      set(draft => {
        // Use our helper function to merge blocks and ensure new references
        const existingBlock = draft.feedbackBlocks.get(namespace);
        draft.feedbackBlocks.set(namespace, mergeBlock(existingBlock, block));
      });
    },
    
    getFeedbackBlock: (namespace: string) => {
      return get().feedbackBlocks.get(namespace);
    },
    
    reset: () => {
      // With immer, we can directly reset the state
      set(draft => {
        draft.lessonSchema = null;
        draft.schemaFieldOrder = [];
        draft.pagesData = new Map();
        draft.feedbackBlocks = new Map();
        draft.currentPageIndex = 0;
      });
    }
  }))
);

// Utility hooks for easier access - using useCallback to prevent unnecessary re-renders

// Get the current page based on currentPageIndex
export const useCurrentPage = () => {
  return useMultiPageLessonStore(state => state.pagesData.get(state.currentPageIndex));
};

// Get a specific page by index with memoized selector
export const usePageByIndex = (pageIndex: number) => {
  return useMultiPageLessonStore(
    state => state.pagesData.get(pageIndex),
    shallow
  );
};

// Get total number of pages
export const useTotalPages = () => {
  return useMultiPageLessonStore(state => state.getTotalPages());
};

// Get current page index
export const useCurrentPageIndex = () => {
  return useMultiPageLessonStore(state => state.currentPageIndex);
};

// Get a specific block by page index and field name (fine-grained updates)
// NOTE: When updating blocks, always create a new block object reference (not just mutate an existing one)
// to ensure that components using this hook will re-render appropriately
export const useBlock = (pageIndex: number, fieldName: string) => {
  return useMultiPageLessonStore(
    state => state.pagesData.get(pageIndex)?.blocks[fieldName]
    // Using default Object.is equality check which is more efficient for single objects than shallow
  );
};

// Get all blocks for the current page - more efficient than using useCurrentPage().blocks
export const useBlocksForCurrentPage = () => {
  return useMultiPageLessonStore(
    state => {
      const currentPage = state.pagesData.get(state.currentPageIndex);
      return currentPage ? Object.values(currentPage.blocks) : [];
    },
    shallow // Use shallow comparison since we're comparing arrays
  );
};

// Get a specific feedback block by its namespace (fine-grained updates)
export const useFeedbackBlock = (namespace: string) => {
  return useMultiPageLessonStore(
    state => state.getFeedbackBlock(namespace),
    // Using default Object.is equality check which is efficient for single objects
  );
};

// Get user action payload
export const useUserActionPayload = () => {
  return useMultiPageLessonStore(
    state => state.getUserActionPayload(),
    shallow // Use shallow comparison since we're comparing a Map
  );
};

// Set user action payload
export const useSetUserActionPayload = () => {
  return useMultiPageLessonStore(state => state.setUserActionPayload);
};

// Check if the current lesson is a multi-page lesson
export const useIsMultiPageLesson = () => {
  return useMultiPageLessonStore(state => {
    // A lesson is considered multi-page if:
    // 1. We have a lesson schema
    // 2. The schema has top-level object properties that themselves contain block definitions
    if (!state.lessonSchema || !state.lessonSchema.properties) {
      return false;
    }
    
    const properties = state.lessonSchema.properties;
    const propertyNames = Object.keys(properties);
    
    // Check if any top-level property is an object that contains nested blocks
    // This indicates a multi-page structure
    return propertyNames.some(name => {
      const prop = properties[name];
      // Check if it's an object reference with $ref or a direct object with properties
      return (
        prop && 
        typeof prop === 'object' && 
        // It's a container if it has a $ref OR properties
        (prop.$ref || (prop.properties && Object.keys(prop.properties).length > 0))
      );
    });
  });
};
